import { apiDate, localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Badge,
    Button,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import {
    BuildingOfficeIcon,
    ChatBubbleLeftRightIcon,
    EnvelopeIcon,
    PhoneIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';
import dayjs from 'dayjs';

const channels = {
    phone: { label: 'Telefoon', Icon: PhoneIcon },
    email: { label: 'E-mail', Icon: EnvelopeIcon },
    customer: { label: 'Klant', Icon: BuildingOfficeIcon },
    whatsapp: { label: 'WhatsApp', Icon: ChatBubbleLeftRightIcon },
};

const sentState = item =>
    item.sent_at
        ? { state_label: 'Verstuurd', state_color: 'green' }
        : { state_label: 'In wachtrij', state_color: 'blue' };

const buildTimeline = communication =>
    [
        ...(communication.phone_communications ?? []).map(phone => ({
            key: `phone-${phone.id}`,
            channel: 'phone',
            state_label: phone.state_label,
            state_color: phone.state_color,
            scheduled_at: phone.scheduled_at,
            done_at: phone.called_at,
            caller: phone.caller,
            note: phone.note,
        })),
        ...(communication.email_communications ?? []).map(email => ({
            key: `email-${email.id}`,
            channel: 'email',
            ...sentState(email),
            scheduled_at: email.scheduled_at,
            done_at: email.sent_at,
        })),
        ...(communication.customer_communications ?? []).map(customer => ({
            key: `customer-${customer.id}`,
            channel: 'customer',
            state_label: customer.state_label,
            state_color: customer.state_color,
            scheduled_at: customer.scheduled_at,
            done_at: null,
        })),
        ...(communication.whatsapp_communications ?? []).map(whatsapp => ({
            key: `whatsapp-${whatsapp.id}`,
            channel: 'whatsapp',
            ...sentState(whatsapp),
            scheduled_at: whatsapp.scheduled_at,
            done_at: whatsapp.sent_at,
        })),
    ].sort((a, b) => {
        const date = item => item.done_at ?? item.scheduled_at;

        return (
            (date(a) ? dayjs(date(a)).valueOf() : Infinity) -
            (date(b) ? dayjs(date(b)).valueOf() : Infinity)
        );
    });

const CommunicationRow = ({ item }) => {
    const { label, Icon } = channels[item.channel] ?? channels.email;

    return (
        <TableRow>
            <TableCell>
                <span className='inline-flex items-center gap-2'>
                    <Icon aria-hidden='true' className='size-4 shrink-0' />
                    {label}
                </span>
            </TableCell>
            <TableCell>{localDate(item.scheduled_at)}</TableCell>
            <TableCell>{localDate(item.done_at)}</TableCell>
            <TableCell>
                <Badge color={item.state_color}>{item.state_label}</Badge>
            </TableCell>
            <TableCell>
                {item.caller && <p>{item.caller}</p>}
                {item.note && (
                    <p className='whitespace-pre-line text-gray-500'>
                        {item.note}
                    </p>
                )}
            </TableCell>
        </TableRow>
    );
};

const AUTHORIZATION = 1;

const CommunicationTimeline = ({ communication }) => {
    const { employee } = usePage().props;
    const timeline = buildTimeline(communication);
    const { confirm, setLoading } = useConfirmationStore();

    const sendReminder = () =>
        confirm({
            title: 'Reminder versturen?',
            description: `Weet je zeker dat je een reminder wilt versturen? De werknemer ontvangt direct een e-mail met ${
                communication.type === AUTHORIZATION
                    ? 'de machtiging'
                    : 'de vragenlijst'
            }.`,
            confirmLabel: 'Versturen',
            onConfirm: close => {
                setLoading(true);

                router.post(
                    `/customers/${employee.customer_id}/employees/${employee.id}/communications/${communication.id}/channels`,
                    {
                        type: 'email',
                        scheduled_at: apiDate(new Date()),
                    },
                    {
                        reset: [
                            'employee',
                            'communicable_subjects',
                            'navigation',
                        ],
                        preserveScroll: true,
                        preserveState: true,
                        onFinish: close,
                    }
                );
            },
        });

    return (
        <>
            <Divider content='Communicatie' />

            <div className='my-4 flex flex-wrap items-center justify-between gap-2'>
                <Badge color={communication.state_color}>
                    {communication.state_label}
                </Badge>
                <div className='flex items-center gap-2'>
                    {communication?.state === 1 && (
                        <>
                            <Button type='button' onClick={sendReminder}>
                                <EnvelopeIcon className='size-4' />
                                Reminder versturen
                            </Button>
                            <Button
                                as={Link}
                                href={`/customers/${employee.customer_id}/employees/${employee.id}/communications/${communication.id}`}
                            >
                                <PhoneIcon className='size-4' />
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {timeline.length === 0 ? (
                <p className='text-sm text-gray-500'>
                    Nog geen berichten of belmomenten.
                </p>
            ) : (
                <div className='mt-2'>
                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Kanaal</TableHeaderCell>
                                <TableHeaderCell>Gepland op</TableHeaderCell>
                                <TableHeaderCell>Uitgevoerd op</TableHeaderCell>
                                <TableHeaderCell>Status</TableHeaderCell>
                                <TableHeaderCell>Details</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {timeline.map(item => (
                                <CommunicationRow key={item.key} item={item} />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </>
    );
};

export default CommunicationTimeline;
